import React, { useEffect, useMemo, useState } from 'react';
import { PageSubheader, TicketDetailModal, CreateTicketModal } from '../shared';
import { ComplianceIssuesTable } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { ComplianceIssueRow } from './callCenterComplianceIssuesTypes';
import { useTicketsStore } from '../../../store/ticketsStore';
import { uiPriority, uiStatus } from '../shared/ticketAdapter';
import { Ticket } from '../../../services/ticketsApi';
import styles from './CallCenterComplianceIssues.module.css';

function adapt(t: Ticket): ComplianceIssueRow {
  return {
    id: String(t.id),
    ticketId: `#${t.ticket_number}`,
    customer: t.customer_name || '—',
    productCategory: '—',
    violationType: t.subject,
    priority: uiPriority(t.priority),
    status: uiStatus(t.status),
  };
}

const CallCenterComplianceIssues: React.FC = () => {
  const items = useTicketsStore((s) => s.items);
  const loading = useTicketsStore((s) => s.loading);
  const error = useTicketsStore((s) => s.error);
  const setFilters = useTicketsStore((s) => s.setFilters);
  const fetchList = useTicketsStore((s) => s.fetchList);

  useEffect(() => {
    setFilters({ category: 'compliance' });
    fetchList();
  }, [setFilters, fetchList]);

  const rows = useMemo<ComplianceIssueRow[]>(() => items.map(adapt), [items]);
  const [openTicketId, setOpenTicketId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className={styles.container}>
      <PageSubheader
        description="Regulatory problems, license violations, restricted products"
        onAction={() => setShowCreate(true)}
      />
      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && rows.length === 0}
        emptyMessage="No compliance issues to display"
        onRetry={() => fetchList()}
      >
        <ComplianceIssuesTable rows={rows} onView={(id) => setOpenTicketId(parseInt(id, 10))} />
      </AsyncBoundary>
      <TicketDetailModal ticketId={openTicketId} onClose={() => setOpenTicketId(null)} />
      <CreateTicketModal
        open={showCreate}
        defaultCategory="compliance"
        lockCategory
        onClose={() => setShowCreate(false)}
        onCreated={(id) => { setOpenTicketId(id); fetchList(); }}
      />
    </div>
  );
};

export const CallCenterComplianceIssuesPage: React.FC = () => <CallCenterComplianceIssues />;

export { CallCenterComplianceIssues };
export default CallCenterComplianceIssues;
